import React, { useState } from "react";
import { YStack, XStack, Text, View, Circle } from "tamagui";
import { RiArrowDownSLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import type { RefundOrderRecord, RefundOrders } from "@screen/Home/pages/OrderList/type/orderList";
import {
  REFUND_METHOD_LABEL,
  REFUND_STATUS_HINT,
  buildRefundSteps,
  etaLabel,
  getRefundCardBadge,
  hasRefundOrders,
  isRefundOpen,
  refundScopeLabelForRecord,
  sortRefundRecords,
  type RefundStep,
} from "@utils/func/refundDisplay";

const ACCENT = colors.amber;
const DEEP = "#B45309";
const TEXT_DEEP = colors.amberDark;
const DONE_BG = "#F5EBDC";

const money = (n: number) => `NT$ ${Math.round(n).toLocaleString("en-US")}`;

const badgeStyle = (record: RefundOrderRecord) => {
  if (record.status === "done") return { bg: DONE_BG, fg: TEXT_DEEP, dot: DEEP };
  if (record.status === "cancel") return { bg: colors.bgGray1, fg: colors.gray, dot: colors.gray200 };
  return { bg: colors.bgAmber, fg: DEEP, dot: ACCENT };
};

const StatusBadge = ({ record }: { record: RefundOrderRecord }) => {
  const style = badgeStyle(record);
  return (
    <XStack ai="center" gap={5} bg={style.bg} px={8} py={3} br={20} flexShrink={0}>
      <View w={5} h={5} br={3} bg={style.dot} />
      <Text fontSize={11} fow="700" color={style.fg}>
        {getRefundCardBadge(record)}
      </Text>
    </XStack>
  );
};

const StepCircle = ({ step, size = 26 }: { step: RefundStep; size?: number }) => {
  const filled = step.state !== "todo";
  const isWarn = step.icon === "warn";
  const fontSize = size <= 22 ? 11 : 12;
  return (
    <Circle
      size={size}
      bg={filled ? ACCENT : colors.light}
      ai="center"
      jc="center"
      borderWidth={filled ? 0 : 1.5}
      borderColor={colors.border}
      flexShrink={0}>
      {isWarn ? (
        <Text fontSize={fontSize + 1} fow="800" color={colors.light}>
          !
        </Text>
      ) : step.icon === "check" || step.state === "done" || step.state === "active" ? (
        <Text fontSize={fontSize} fow="800" color={filled ? colors.light : colors.hint}>
          ✓
        </Text>
      ) : (
        <Text fontSize={fontSize - 1} fow="700" color={colors.hint}>
          {step.stepNumber ?? ""}
        </Text>
      )}
    </Circle>
  );
};

/** 收合用：只顯示 icon 節點與連線 */
const CompactTimeline = ({ steps }: { steps: RefundStep[] }) => (
  <XStack ai="center" w="100%">
    {steps.map((step, i) => {
      const last = i === steps.length - 1;
      const lineDone = step.state === "done" && !last && steps[i + 1]?.state !== "todo";
      return (
        <React.Fragment key={`${step.title}-${i}`}>
          <StepCircle step={step} size={18} />
          {!last && (
            <View f={1} h={1.5} bg={lineDone || step.state === "done" ? ACCENT : colors.border} mx={3} br={1} minWidth={8} />
          )}
        </React.Fragment>
      );
    })}
  </XStack>
);

/** 展開用：icon + 步驟標題 */
const HorizontalTimeline = ({ steps }: { steps: RefundStep[] }) => (
  <XStack ai="flex-start" w="100%" jc="space-between" px={2}>
    {steps.map((step, i) => {
      const last = i === steps.length - 1;
      const lineDone = step.state === "done" && !last && steps[i + 1]?.state !== "todo";
      return (
        <React.Fragment key={`${step.title}-${i}`}>
          <YStack ai="center" gap={6} f={1}>
            <StepCircle step={step} />
            <Text
              fontSize={11}
              fow={step.state === "todo" ? "500" : "700"}
              color={step.state === "todo" ? colors.hint : TEXT_DEEP}
              ta="center"
              whiteSpace="nowrap">
              {step.title}
            </Text>
          </YStack>
          {!last && (
            <View
              f={0.55}
              h={2}
              bg={lineDone || step.state === "done" ? ACCENT : colors.border}
              mt={12}
              mx={-6}
              br={1}
            />
          )}
        </React.Fragment>
      );
    })}
  </XStack>
);

const KV = ({ k, v }: { k: string; v: React.ReactNode }) => (
  <XStack jc="space-between" ai="flex-start" gap="$3">
    <Text fontSize={13} color={colors.sub} flexShrink={0}>
      {k}
    </Text>
    <Text fontSize={13.5} fow="600" color={colors.text} ta="right" f={1}>
      {v}
    </Text>
  </XStack>
);

const Notice = ({ text, tone }: { text: string; tone: "info" | "ok" | "warn" }) => {
  const bg = tone === "ok" ? "#ECFDF5" : colors.bgAmber;
  const fg = tone === "ok" ? "#047857" : TEXT_DEEP;
  const icon = tone === "ok" ? "✓" : tone === "warn" ? "!" : "i";
  return (
    <XStack bg={bg} br={10} px={12} py={10} gap={8} ai="flex-start">
      <Circle size={18} bg={tone === "ok" ? "#10B981" : ACCENT} ai="center" jc="center" mt={1}>
        <Text fontSize={10} fow="800" color={colors.light}>
          {icon}
        </Text>
      </Circle>
      <Text fontSize={12} lineHeight={18} color={fg} f={1}>
        {text}
      </Text>
    </XStack>
  );
};

/** 高度 + 透明度收合動畫（web grid 0fr/1fr） */
const AccordionBody = ({ open, children }: { open: boolean; children: React.ReactNode }) => (
  <div
    style={{
      display: "grid",
      gridTemplateRows: open ? "1fr" : "0fr",
      transition: "grid-template-rows 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
    }}>
    <div style={{ overflow: "hidden", minHeight: 0 }}>
      <div
        style={{
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0)" : "translateY(-6px)",
          transition: "opacity 0.22s ease, transform 0.22s ease",
          pointerEvents: open ? "auto" : "none",
          paddingTop: 12,
        }}>
        {children}
      </div>
    </div>
  </div>
);

/**
 * 單筆退款手風琴（扁平 + divider，無灰底區塊）。
 * 收合：精簡 timeline icons + 金額；展開：完整進度、明細、提示。
 */
const RefundRecordBlock = ({
  record,
  orderTotal,
  defaultOpen,
  showDivider,
}: {
  record: RefundOrderRecord;
  orderTotal: number;
  defaultOpen: boolean;
  showDivider: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const steps = buildRefundSteps(record);
  const method = REFUND_METHOD_LABEL[record.refund_method] ?? record.refund_method;
  const scope = refundScopeLabelForRecord(record, orderTotal);
  const hint = REFUND_STATUS_HINT[record.status];
  const eta = etaLabel(record);
  const activeStep = steps.find((s) => s.state === "active") ?? steps[steps.length - 1];
  const noticeTone =
    record.status === "done" ? "ok" : record.status === "failed" || record.status === "manual" ? "warn" : "info";

  return (
    <YStack
      pt={showDivider ? 12 : 0}
      borderTopWidth={showDivider ? 1 : 0}
      borderTopColor={colors.borderLight}>
      <XStack
        ai="center"
        gap={8}
        py={4}
        onPress={() => setOpen((v) => !v)}
        pressStyle={{ opacity: 0.75 }}
        cursor="pointer"
        role="button"
        aria-expanded={open}>
        {open ? (
          <XStack ai="center" gap={6} f={1} minWidth={0}>
            <Text fontSize={13} fow="700" color={colors.text}>
              退款單
            </Text>
            <Text fontSize={11} color={colors.hint} numberOfLines={1}>
              #{record.refund_id}
            </Text>
            <View f={1} />
            <StatusBadge record={record} />
          </XStack>
        ) : (
          <XStack ai="center" gap={10} f={1} minWidth={0}>
            <View w={88} flexShrink={0}>
              <CompactTimeline steps={steps} />
            </View>
            <YStack f={1} minWidth={0} gap={0}>
              <XStack ai="baseline" gap={5} flexWrap="wrap">
                <Text fontSize={13} fow="800" color={colors.text}>
                  {money(record.actual_refund)}
                </Text>
                <Text fontSize={10} fow="600" color={colors.sub}>
                  {scope}
                </Text>
              </XStack>
              <Text fontSize={10} color={colors.hint} numberOfLines={1}>
                #{record.refund_id}
                {activeStep ? ` · ${activeStep.title}` : ""}
              </Text>
            </YStack>
            <StatusBadge record={record} />
          </XStack>
        )}

        <View
          style={{
            transform: `rotate(${open ? 180 : 0}deg)`,
            transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
          <RiArrowDownSLine size={18} color={colors.hint} />
        </View>
      </XStack>

      <AccordionBody open={open}>
        <YStack gap={12}>
          <HorizontalTimeline steps={steps} />
          <YStack gap={10}>
            <KV
              k="退款金額"
              v={
                <Text fontSize={13.5} fow="700" color={colors.text}>
                  {money(record.actual_refund)}{" "}
                  <Text fontSize={12} fow="600" color={colors.sub}>
                    ({scope})
                  </Text>
                </Text>
              }
            />
            {!!record.reason && <KV k="退款原因" v={record.reason} />}
            <KV k="退回方式" v={record.status === "manual" || record.status === "failed" ? "匯款退回" : method} />
            {eta && <KV k="預計到帳" v={eta} />}
          </YStack>
          {!!hint && <Notice text={hint} tone={noticeTone} />}
        </YStack>
      </AccordionBody>
    </YStack>
  );
};

/**
 * 有金錢退款時顯示的進度卡（取代配送 timeline）。
 * 多筆以 divider 分隔；進行中預設展開。
 */
export const RefundCard = ({ refunds, orderTotal }: { refunds?: RefundOrders; orderTotal: number }) => {
  if (!hasRefundOrders(refunds)) return null;

  const records = sortRefundRecords(refunds!.records);

  return (
    <YStack bg={colors.light} br={12} p="$4" gap={10} shadowColor="rgba(0,0,0,0.05)" shadowRadius={5}>
      <XStack ai="center" gap={8}>
        <Circle size={22} bg={colors.bgAmber} ai="center" jc="center">
          <Text fontSize={12} fow="800" color={DEEP}>
            $
          </Text>
        </Circle>
        <Text fontSize={15} fow="700" color={colors.text}>
          退款進度
        </Text>
        {records.length > 1 && (
          <Text fontSize={12} color={colors.hint} fow="600">
            共 {records.length} 筆
          </Text>
        )}
      </XStack>

      <View h={1} bg={colors.borderLight} />

      <YStack>
        {records.map((r, i) => (
          <RefundRecordBlock
            key={r.refund_id}
            record={r}
            orderTotal={orderTotal}
            defaultOpen={isRefundOpen(r.status)}
            showDivider={i > 0}
          />
        ))}
      </YStack>
    </YStack>
  );
};

export default RefundCard;
